import { layout } from '../../App.routing'

import {
    ContentDataTypes,
    GetRouteAdTargeting,
    PageType,
    StaticRoutes,
    Topic,
} from '@news-mono/web-common'

import { BaseClientConfig } from '@news-mono/web-common'
import { getClaremontSerialKillingsRouteInfo } from './custom/get-claremont-serial-killings-route-info'
import { getSubscriberFeaturesRouteInfo } from './custom/get-subscriber-features-route-info'

import { getKerryStokesCollectionRouteInfo } from './custom/get-kerry-stokes-collection-route-info'
import { getYourMoneyRouteInfo } from './custom/get-your-money-route-info'
import { getCricketRouteInfo } from './custom/get-cricket-route-info'
import { getGerardRossRouteInfo } from './custom/get-gerard-ross-route-info'
import { topicParallax } from './topic-parallax'
import { getAFLDraftRouteInfo } from './custom/get-afl-draft-route'
import { getRTS2019RouteInfo } from './custom/get-rts-2019-route'
import { getCarOfTheYear2019RouteInfo } from './custom/get-car-of-the-year-2019-route-info'
import { AdTargeting } from '@news-mono/web-common'
import { getBreakfastBrunchRouteInfo } from './custom/get-breakfast-brunch-route'
import { CheckedComponentInformation } from 'json-react-layouts'
import { getLifestyleNewHomesRouteInfo } from './custom/get-lifestyle-new-homes-route-info'
import {
    SierraRouteProps,
    LimaRouteProps,
    TheWestSection,
    ResponsivePictureLayout,
    metrics,
} from '@news-mono/component-library'
import { getAFLRouteInfo } from './custom/get-afl-route-info'
import { getBusinessRouteInfo } from './custom/get-business-route-info'
import { getEntertainmentRouteInfo } from './custom/get-entertainment-route-info'
import { getInsideCoverRouteInfo } from './custom/get-insidecover-route-info'
import { getLatestRouteInfo } from './custom/get-latest-route-info'
import { getLifestyleRouteInfo } from './custom/get-lifestyle-route-info'
import { getNewsRouteInfo } from './custom/get-news-route-info'
import { getOpinionRouteInfo } from './custom/get-opinion-route-info'
import { getPoliticsRoute } from './custom/get-politics-route'
import { getRegionalRouteInfo } from './custom/get-regional-route-info'
import { getSportRouteInfo } from './custom/get-sport-route-info'
import { getTechnologyRouteInfo } from './custom/get-technology-route-info'
import { getTravelRouteInfo } from './custom/get-travel-route-info'
import { getTopicSponsor } from './get-topic-sponsor'
import { getCarOfTheYear2020RouteInfo } from './custom/get-car-of-the-year-2020-route-info'

interface TopicSectionBuilderHooks {
    sierra?: SierraRouteProps & { dataDefinitionArgs: ContentDataTypes }
    lima?: LimaRouteProps & { dataDefinitionArgs: ContentDataTypes }
}

export interface TopicSectionBuilder {
    getCuratedSection: (
        hooks?: TopicSectionBuilderHooks,
    ) => Array<CheckedComponentInformation>
}

export type GetMainSectionFn = (
    topicSectionBuilder: TopicSectionBuilder,
) => Array<CheckedComponentInformation>

const createPage = ({
    topic,
    pageTitle,
    getMainSection,
    curationName,
    section = 'default',
    getAdTargeting,
}: {
    config: BaseClientConfig
    topic: Topic
    pageTitle: string
    getMainSection: GetMainSectionFn
    curationName?: string
    section?: TheWestSection
    getAdTargeting: GetRouteAdTargeting
}): PageType<TheWestSection> => {
    const { adUnitPath, ssAdUnits, topics } = getAdTargeting(
        'home',
        section,
        topic,
    )

    const adTargeting: AdTargeting = {
        pageId: topic.title,
        adUnitPath,
        ssAdUnits,
        topics,
    }

    const getCuratedSection: TopicSectionBuilder['getCuratedSection'] = (
        hooks,
    ) => {
        const sierraHookProps = hooks ? hooks.sierra || {} : {}
        const limaHookProps = hooks ? hooks.lima || {} : {}

        return [
            layout.component({
                type: 'sierra',
                props: {
                    verticalSpacing: 'gridGap',
                    fixedRatios: ['16:9'],
                    heroImageLayout: ResponsivePictureLayout.ObjectFitContain,
                    cardOrientation: {
                        type: 'Landscape-Portrait',
                        maxBreakpoint: 'md',
                    },
                    cardLayout: ['hero', 'teaser-xsVisible-mdHidden-Image'],

                    dataDefinitionArgs: {
                        type: 'curation',
                        name: curationName || topic.id,
                        offset: 0,
                        pageSize: 2,
                    },
                    ...sierraHookProps,
                },
            }),
            layout.component({
                type: 'lima',
                props: {
                    verticalSpacing: 'xl',
                    fontScale: 1.2,
                    fixedRatios: ['16:9'],
                    hasBackground: true,
                    initialColumns: 1,
                    intermediateColumns: 2,
                    finalColumns: 4,
                    teaserMode: 'visible',
                    cardOrientation: { type: 'Landscape-Portrait' },

                    dataDefinitionArgs: {
                        type: 'curation',
                        name: curationName || topic.id,
                        offset: 2,
                        pageSize: 4,
                    },
                    ...limaHookProps,
                },
            }),
            ...topicParallax(),
        ]
    }

    return {
        kind: 'page',
        heading: pageTitle,
        pageType: 'home',
        adTargeting,
        hideHeading: true,
        includeFromSectionInMetaDescription: !topic.seoDescription,
        pageMeta: {
            description:
                topic.seoDescription || `The Latest in ${topic.seoTitle}`,
            title: topic.seoTitle || topic.title,
        },
        section,
        compositions: [
            layout.composition({
                type: 'box',
                props: {
                    containerWidth:
                        metrics.thewest.siteMetrics.mainContentWidth,
                    horizontalGutters: 'outerMargin',
                    verticalGutters: ['outerMargin', 'unset'],
                },
                contentAreas: {
                    main: [
                        layout.component({
                            type: 'breaking-news',
                            props: {
                                dataDefinitionArgs: {
                                    type: 'curation',
                                    name: 'breaking-news',
                                    offset: 0,
                                    pageSize: 1,
                                },
                            },
                        }),
                        layout.component({
                            type: 'breadcrumb',
                            props: {
                                topic,
                                sponsor: getTopicSponsor(topic),
                                enableHeaderTag: true,
                            },
                        }),
                    ],
                },
            }),
            layout.composition({
                type: 'thor',
                props: {
                    containerWidth:
                        metrics.thewest.siteMetrics.mainContentWidth,
                    horizontalGutters: true,
                },
                contentAreas: {
                    main: getMainSection({ getCuratedSection }),
                },
            }),
        ],
    }
}

export const customTopicRoutes: StaticRoutes<TheWestSection> = {
    '/latest': getLatestRouteInfo({
        id: 'latest',
        title: 'Latest News',
        seoTitle: 'Latest News',
        metadata: {},
    }),
    '/features': ({ config, getAdTargeting }) =>
        createPage({
            config,
            topic: {
                id: 'features',
                title: 'Subscriber Exclusive',
                seoTitle: 'Subscriber Exclusive',
                metadata: {},
            },
            pageTitle: 'Subscriber Exclusive',
            getMainSection: getSubscriberFeaturesRouteInfo,
            curationName: 'features',
            getAdTargeting,
        }),
    '/business': ({ config, getAdTargeting }) =>
        createPage({
            config,
            topic: {
                id: 'business',
                title: 'Business',
                seoTitle: 'Business',
                metadata: {},
            },
            pageTitle: 'Business',
            getMainSection: getBusinessRouteInfo,
            getAdTargeting,
        }),
    '/entertainment': ({ config, getAdTargeting }) =>
        createPage({
            config,
            topic: {
                id: 'entertainment',
                title: 'Entertainment',
                seoTitle: 'Entertainment',
                metadata: {},
            },
            pageTitle: 'Entertainment',
            getMainSection: getEntertainmentRouteInfo,
            getAdTargeting,
        }),
    '/lifestyle': ({ config, getAdTargeting }) =>
        createPage({
            config,
            topic: {
                id: 'lifestyle',
                title: 'Lifestyle',
                seoTitle: 'Lifestyle',
                metadata: {},
            },
            pageTitle: 'Lifestyle',
            getMainSection: getLifestyleRouteInfo,
            getAdTargeting,
        }),
    '/news': ({ config, getAdTargeting }) =>
        createPage({
            config,
            topic: {
                id: 'news',
                title: 'News',
                seoTitle: 'News',
                metadata: {},
            },
            pageTitle: 'News',
            getMainSection: getNewsRouteInfo,
            getAdTargeting,
        }),
    '/opinion': ({ config, getAdTargeting }) =>
        createPage({
            config,
            topic: {
                id: 'opinion',
                title: 'Opinion',
                seoTitle: 'Opinion',
                metadata: {},
            },
            pageTitle: 'Opinion',
            getMainSection: getOpinionRouteInfo,
            getAdTargeting,
        }),
    '/opinion/inside-cover': ({ config, getAdTargeting }) =>
        createPage({
            config,
            topic: {
                id: 'opinion/inside-cover',
                title: 'Inside Cover',
                seoTitle: 'Inside Cover',
                metadata: {},
            },
            pageTitle: 'Inside Cover',
            getMainSection: getInsideCoverRouteInfo,
            getAdTargeting,
        }),
    '/lifestyle/new-homes': ({ config, getAdTargeting }) =>
        createPage({
            config,
            topic: {
                id: 'lifestyle/new-homes',
                title: 'New Homes',
                seoTitle: 'New Homes',
                metadata: {},
            },
            pageTitle: 'New Homes',
            getMainSection: getLifestyleNewHomesRouteInfo,
            getAdTargeting,
        }),
    '/lifestyle/perth-breakfast-brunch': ({ config, getAdTargeting }) =>
        createPage({
            config,
            topic: {
                id: 'lifestyle/perth-breakfast-brunch',
                title: 'Perth Breakfast and Brunch',
                seoTitle: 'Perth Breakfast and Brunch',
                metadata: {},
            },
            pageTitle: 'Perth Breakfast and Brunch',
            getMainSection: getBreakfastBrunchRouteInfo,
            getAdTargeting,
        }),
    '/politics': ({ config, getAdTargeting }) =>
        createPage({
            config,
            topic: {
                id: 'politics',
                title: 'Politics',
                seoTitle: 'Politics',
                metadata: {},
            },
            pageTitle: 'Politics',
            getMainSection: getPoliticsRoute,
            getAdTargeting,
        }),
    '/sport': ({ config, getAdTargeting }) =>
        createPage({
            config,
            topic: {
                id: 'sport',
                title: 'Sport',
                seoTitle: 'Sport',
                metadata: {},
            },
            pageTitle: 'Sport',
            getMainSection: getSportRouteInfo,
            getAdTargeting,
        }),
    '/technology': ({ config, getAdTargeting }) =>
        createPage({
            config,
            topic: {
                id: 'technology',
                title: 'Technology',
                seoTitle: 'Technology',
                metadata: {},
            },
            pageTitle: 'Technology',
            getMainSection: getTechnologyRouteInfo,
            getAdTargeting,
        }),
    '/travel': ({ config, getAdTargeting }) =>
        createPage({
            config,
            topic: {
                id: 'travel',
                title: 'Travel',
                seoTitle: 'Travel',
                metadata: {},
            },
            pageTitle: 'Travel',
            getMainSection: getTravelRouteInfo,
            getAdTargeting,
        }),
    '/news/regional': ({ config, getAdTargeting }) =>
        createPage({
            config,
            topic: {
                id: 'news/regional',
                title: 'Regional',
                seoTitle: 'Regional',
                metadata: {},
            },
            pageTitle: 'Regional',
            getMainSection: getRegionalRouteInfo,
            curationName: 'regional',
            getAdTargeting,
        }),
    '/news/claremont-serial-killings': getClaremontSerialKillingsRouteInfo({
        id: 'news/claremont-serial-killings',
        title: 'Claremont Serial Killings',
        seoTitle: 'Claremont Serial Killings',
        metadata: {},
        parent: { id: 'news', title: 'News', seoTitle: 'News', metadata: {} },
    }),
    '/sport/afl': ({ config, getAdTargeting }) =>
        createPage({
            config,
            topic: { id: 'afl', title: 'AFL', seoTitle: 'AFL', metadata: {} },
            pageTitle: 'AFL',
            getMainSection: getAFLRouteInfo,
            getAdTargeting,
        }),
    '/sport/afl-draft': ({ config, getAdTargeting }) =>
        createPage({
            config,
            topic: {
                id: 'afl-draft',
                title: 'AFL Draft',
                seoTitle: 'AFL Draft',
                metadata: {},
            },
            pageTitle: 'AFL Draft',
            getMainSection: getAFLDraftRouteInfo,
            getAdTargeting,
        }),
    '/features/kerry-stokes-collection': ({ config, getAdTargeting }) =>
        createPage({
            config,
            topic: {
                id: 'features/kerry-stokes-collection',
                title: 'The Kerry Stokes Collection',
                seoTitle: 'The Kerry Stokes Collection',
                metadata: {},
                parent: {
                    id: 'features',
                    title: 'Features',
                    metadata: {},
                    seoTitle: 'Features',
                },
            },
            pageTitle: 'The Kerry Stokes Collection',
            getMainSection: getKerryStokesCollectionRouteInfo,
            getAdTargeting,
        }),
    '/business/your-money': ({ config, getAdTargeting }) =>
        createPage({
            config,
            topic: {
                id: 'your-money',
                title: 'Your Money',
                seoTitle: 'Your Money',
                metadata: {},
                parent: {
                    id: 'business',
                    title: 'Business',
                    seoTitle: 'Business',
                    metadata: {},
                },
            },
            pageTitle: 'Your Money',
            getMainSection: getYourMoneyRouteInfo,
            getAdTargeting,
        }),
    '/sport/cricket': ({ config, getAdTargeting }) =>
        createPage({
            config,
            topic: {
                id: 'sport/cricket',
                title: 'Cricket',
                seoTitle: 'Cricket',
                metadata: {},
                parent: {
                    id: 'sport',
                    title: 'Sport',
                    seoTitle: 'Sport',
                    metadata: {},
                },
            },
            pageTitle: 'Cricket',
            getMainSection: getCricketRouteInfo,
            curationName: 'cricket',
            getAdTargeting,
        }),
    '/features/gerard-ross': ({ config, getAdTargeting }) =>
        createPage({
            config,
            topic: {
                id: 'features/gerard-ross',
                title: 'Gerard Ross',
                seoTitle: 'Gerard Ross',
                metadata: {},
                parent: {
                    id: 'features',
                    title: 'Features',
                    metadata: {},
                    seoTitle: 'Features',
                },
            },
            pageTitle: 'Gerard Ross',
            getMainSection: getGerardRossRouteInfo,
            getAdTargeting,
        }),
    '/features/2019-cars-of-the-year': ({ config, getAdTargeting }) =>
        createPage({
            config,
            topic: {
                id: 'features/2019-cars-of-the-year',
                title: '2019 Cars of the Year',
                seoTitle: '2019 Cars of the Year',
                metadata: {},
                parent: {
                    id: 'features',
                    title: 'Features',
                    metadata: {},
                    seoTitle: 'Features',
                },
            },
            pageTitle: '2019 Cars of the Year',
            getMainSection: getCarOfTheYear2019RouteInfo,
            getAdTargeting,
        }),
    '/features/2020-cars-of-the-year': ({ config, getAdTargeting }) =>
        createPage({
            config,
            topic: {
                id: 'features/2020-cars-of-the-year',
                title: '2020 Cars of the Year',
                seoTitle: '2020 Cars of the Year',
                metadata: {},
                parent: {
                    id: 'features',
                    title: 'Features',
                    metadata: {},
                    seoTitle: 'Features',
                },
            },
            pageTitle: '2020 Cars of the Year',
            getMainSection: getCarOfTheYear2020RouteInfo,
            getAdTargeting,
        }),
    '/business/rts2019': ({ config, getAdTargeting }) =>
        createPage({
            config,
            topic: {
                id: 'rts2019',
                title: 'Resources Technology Showcase 2019',
                seoTitle: 'Resources Technology Showcase 2019',
                metadata: {},
                parent: {
                    id: 'business',
                    title: 'Business',
                    seoTitle: 'Business',
                    metadata: {},
                },
            },
            pageTitle: 'Resources Technology Showcase 2019',
            getMainSection: getRTS2019RouteInfo,
            getAdTargeting,
        }),
}
